import { FeatureFlag } from '@cbo/shared-library';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Fuse from 'fuse.js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsers } from '../../../../contexts/userContext';
import isPermitted, { hasPackagePermissions } from '../../../../lib/permissions';
import { useFeatureFlag } from '../../../../utils/hooks/useFeatureFlag';
import CentralDashboardCard from '../../../../components/CentralDashboard/components/CentralDashboardCard';
import { reportAccessMap } from '../ReportAccessMap';
import ReportIcons from '../ReportIcons/ReportIcons';
import ViewButton from '../ViewButton/ViewButton';
import { boxSx, columnGridSx, rowGridSx, textSx } from './styles';
import { usePackages } from '../../../../contexts/packageContext';
import routes from '../../../../constants/routes';
import { CustomReportSummary } from '../../../requests/index';
import { ScheduledReport } from '../../../../scheduleReports/types';
import ScheduledReportCard from '../../../../scheduleReports/ViewScheduledReports/ScheduledReportCard';

type ReportSectionProps = {
  sectionHeader: string;
  search: string;
  favorites: number[];
  allFavorites?: number[]; // Full favorites list for managing add/remove operations
  customReports: CustomReportSummary[];
  scheduledReports?: ScheduledReport[]; // Scheduled reports to display as cards
  onScheduleReportClick?: (reportId: number | string) => void;
  onPublishReportClick?: (reportId: number, reportName: string) => void;
  onManagePublishReportClick?: (reportId: number, reportName: string) => void;
  onUnPublishReportClick?: (reportId: number, reportName: string) => void;
  onDeleteReportClick?: (reportId: number, reportName: string) => void;
  onFavoritesClick?: (updatedFavorites: number[]) => void;
};

export default function ReportSection({
  sectionHeader,
  search,
  favorites,
  allFavorites,
  customReports,
  scheduledReports = [],
  onScheduleReportClick,
  onPublishReportClick,
  onManagePublishReportClick,
  onUnPublishReportClick,
  onDeleteReportClick,
  onFavoritesClick,
}: Readonly<ReportSectionProps>) {
  const { t } = useTranslation();
  const user = useUsers();
  const packages = usePackages();
  const [expanded, setExpanded] = useState(false);
  // Use allFavorites for managing favorites if provided, otherwise fall back to favorites
  const favoritesForManagement = allFavorites || favorites;
  const map: Partial<Record<FeatureFlag, boolean>> = {
    [FeatureFlag.laborTipReports]: useFeatureFlag(FeatureFlag.laborTipReports),
    [FeatureFlag.perpetualInventoryReport]: useFeatureFlag(FeatureFlag.perpetualInventoryReport),
    [FeatureFlag.lowStockReport]: useFeatureFlag(FeatureFlag.lowStockReport),
    [FeatureFlag.top20ItemsReport]: useFeatureFlag(FeatureFlag.top20ItemsReport),
    [FeatureFlag.realTimeInventory]: useFeatureFlag(FeatureFlag.realTimeInventory),
    [FeatureFlag.cashOffice]: useFeatureFlag(FeatureFlag.cashOffice),
    [FeatureFlag.journalEntry]: useFeatureFlag(FeatureFlag.journalEntry),
    [FeatureFlag.profitLossReport]: useFeatureFlag(FeatureFlag.profitLossReport),
    [FeatureFlag.comparativeSalesLaborReport]: useFeatureFlag(FeatureFlag.comparativeSalesLaborReport),
    [FeatureFlag.hideAlohaEssentialOrgs]: !useFeatureFlag(FeatureFlag.hideAlohaEssentialOrgs),
  };

  const cards: JSX.Element[] = [];
  const cardData: { card: JSX.Element; title: string; description: string }[] = [];
  const isCustomReportsSection = sectionHeader === t('admin.centralReportsPage.sectionHeaders.customReports');

  Object.keys(reportAccessMap).forEach((route) => {
    const config = reportAccessMap[route];

    const hasRequiredPackage = !config.packagesRequired || hasPackagePermissions(packages, config.packagesRequired);

    if (
      isPermitted(user, config.permissions) &&
      (!config.featureFlag || map[config.featureFlag]) &&
      hasRequiredPackage
    ) {
      if (
        t(config.section) === sectionHeader ||
        (sectionHeader === t('admin.centralReportsPage.sectionHeaders.favorites') && favorites.includes(config.id))
      ) {
        const card = (
          <CentralDashboardCard
            key={route}
            description={t(config.description)}
            title={t(config.reportName)}
            category={t(config.category)}
            url={route}
            isFavorite={favoritesForManagement.includes(config.id)}
            currentFavorites={favoritesForManagement}
            reportId={config.id}
            reportStringId={config.reportId}
            onScheduleReportClick={onScheduleReportClick}
            onPublishReportClick={onPublishReportClick}
            onManagePublishReportClick={onManagePublishReportClick}
            onUnPublishReportClick={onUnPublishReportClick}
            onFavoritesClick={onFavoritesClick}
            onDeleteReportClick={onDeleteReportClick}
          />
        );
        cards.push(card);
        cardData.push({
          card,
          title: t(config.reportName),
          description: t(config.description),
        });
      }
    }
  });

  customReports.forEach((report) => {
    if (
      (sectionHeader === t('admin.centralReportsPage.sectionHeaders.favorites') && favorites.includes(report.id)) ||
      isCustomReportsSection
    ) {
      const card = (
        <CentralDashboardCard
          key={`custom-${report.id}`}
          description={report.description}
          title={report.title}
          category='Custom'
          url={`${routes.CUSTOM_REPORTS}?id=${encodeURIComponent(String(report.id))}&edit=false`}
          isFavorite={favoritesForManagement.includes(report.id)}
          currentFavorites={favoritesForManagement}
          reportId={report.id}
          isPublished={report.isPublished}
          lastPublished={report.lastPublished}
          creator={report.creator}
          onPublishReportClick={onPublishReportClick}
          onManagePublishReportClick={onManagePublishReportClick}
          onUnPublishReportClick={onUnPublishReportClick}
          onScheduleReportClick={onScheduleReportClick}
          onDeleteReportClick={onDeleteReportClick}
          onFavoritesClick={onFavoritesClick}
        />
      );
      cards.push(card);
      cardData.push({
        card,
        title: report.title,
        description: report.description,
      });
    }
  });

  // Add scheduled report cards if provided
  scheduledReports.forEach((report) => {
    const card = <ScheduledReportCard key={`scheduled-report-${report.id}`} report={report} hideChipsSection />;
    cards.push(card);
    cardData.push({
      card,
      title: report.scheduleName,
      description: report.description ?? '',
    });
  });

  const sortedCardData = !isCustomReportsSection
    ? [...cardData].sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }))
    : cardData;

  // Fuzzy search using Fuse.js
  const fuse = new Fuse(sortedCardData, {
    keys: ['title', 'description'],
    threshold: 0.4,
  });

  const filteredCards =
    search.trim() === ''
      ? sortedCardData.map((card) => card.card)
      : fuse.search(search).map((result) => result.item.card);

  if (!filteredCards.length) return null;

  return (
    <Grid item xs={12} sx={columnGridSx}>
      <Box sx={boxSx}>
        <ReportIcons sectionHeader={sectionHeader} />
        <Typography sx={textSx} color='text.primary'>
          {sectionHeader}
        </Typography>
      </Box>
      <Grid item xs={12} sx={rowGridSx}>
        {expanded ? filteredCards : filteredCards.slice(0, 6)}
      </Grid>
      {filteredCards.length > 6 ? <ViewButton expanded={expanded} setExpanded={setExpanded} /> : null}
    </Grid>
  );
}
